import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { PenTool, Pencil, Trash2, MapPin, QrCode, Tag, Info, AlertTriangle, X as XIcon } from 'lucide-react';
import { renderFieldTypeIcon } from '../../utils/iconUtils';
import { evaluateRules } from '../../utils/ruleEvaluator';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { ThemedText, Input, FileUpload, ConfirmDialog } from '../';
import { MiniMap } from '../MiniMap';
import { validateField } from '../../utils/fieldValidation';
import formSchemaService from '../../api/services/formSchemaService';
import { convertFormSchemaDefinitionToFields } from '../../utils/schemaConverter';
import { RepeatedFormTableDisplay } from './RepeatedFormTableDisplay';
import { FieldSuggestInput } from '../FieldSuggestInput';
import { DrawingCanvas } from './DrawingCanvas';
import { LocationPickerModal } from './LocationPickerModal';
import { VoiceFieldRenderer } from './VoiceFieldRenderer';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$dynamicBg || props.theme.colors.background};
  color: ${props => props.$dynamicFg || props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  min-height: 40px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

/** Small badge shown beside a field label when a format rule matches. */
const FieldFlagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: ${props => props.theme.spacing.xs};
  padding: 2px 8px;
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: ${props => props.theme.typography.weights.semiBold};
  background: ${props => props.$bg || props.theme.colors.tint};
  color: ${props => props.$fg || '#ffffff'};
  border: 1px solid ${props => props.theme.colors.border};
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.xs};
`;

const StyledCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${props => props.theme.colors.tint};
  cursor: pointer;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StyledRadio = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${props => props.theme.colors.tint};
  cursor: pointer;
`;

const ErrorText = styled(ThemedText)`
  color: ${props => props.theme.colors.danger || '#dc3545'};
  font-size: ${props => props.theme.typography.sizes.small};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${props => props.theme.colors.text};
`;

const OptionsGroupBox = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
`;

const FileUploadContainer = styled.div`
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.tint}10;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

// ---- Drawing field styled components ----

const DrawingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const DrawingThumb = styled.div`
  position: relative;
  aspect-ratio: 4/3;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  border: 2px solid ${props => props.theme.colors.border};
  background: #f5f5f5;
  cursor: pointer;

  &:hover > div {
    opacity: 1;
  }
`;

const DrawingThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const DrawingThumbOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.15s;
`;

const ThumbAction = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 15px;
  flex-shrink: 0;

  &:hover {
    background: #ffffff;
  }
`;

const AddDrawingBtn = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: transparent;
  color: ${props => props.theme.colors.tint};
  font-size: ${props => props.theme.typography.sizes.default};
  cursor: pointer;
  width: 100%;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background: ${props => props.theme.colors.tint + '08'};
  }
`;

// ---- Geolocation field styled components ----

const ChangeLocationBtn = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background: ${props => props.theme.colors.tint + '08'};
  }
`;

// ==================== Drawing sub-component ====================

/**
 * DrawingFieldRenderer — manages state for a single drawing field.
 * Must be a separate component so it can use useState.
 */
const DrawingFieldRenderer = ({ field, value, onChange, errors, userId }) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  // Maps blobPath → local data URL (set immediately on save, no read SAS needed)
  const [previewUrls, setPreviewUrls] = useState({});
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);

  const drawings = Array.isArray(value) ? value : (value ? [value] : []);
  const maxDrawings = field.props.maxDrawings || 10;
  const { id, props } = field;
  const fieldError = errors?.[id];

  // onSave receives (blobPath, previewDataUrl) from DrawingCanvas
  const handleSave = (blobPath, previewDataUrl) => {
    const updated = [...drawings];
    if (editIndex !== null) {
      updated[editIndex] = blobPath;
    } else {
      updated.push(blobPath);
    }
    if (previewDataUrl) {
      setPreviewUrls(prev => ({ ...prev, [blobPath]: previewDataUrl }));
    }
    onChange(updated);
    setModalOpen(false);
    setEditIndex(null);
  };

  const handleEdit = (i) => {
    setEditIndex(i);
    setModalOpen(true);
  };

  const handleDelete = (i) => {
    setConfirmDeleteIndex(i);
  };

  const handleAdd = () => {
    setEditIndex(null);
    setModalOpen(true);
  };

  return (
    <FieldContainer theme={theme}>
      <LabelRow theme={theme}>
        {renderFieldTypeIcon(field.type, 13)}
        <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
      </LabelRow>

      {drawings.length > 0 && (
        <DrawingGrid theme={theme}>
          {drawings.map((blobPath, i) => (
            <DrawingThumb key={i} theme={theme}>
              {previewUrls[blobPath] ? (
                <DrawingThumbImg
                  src={previewUrls[blobPath]}
                  alt={`Drawing ${i + 1}`}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#e5e7eb', color: '#9ca3af'
                }}><PenTool size={22} /></div>
              )}
              <DrawingThumbOverlay>
                <ThumbAction title="Edit" onClick={() => handleEdit(i)}><Pencil size={15} /></ThumbAction>
                <ThumbAction title="Delete" onClick={() => handleDelete(i)}><Trash2 size={15} /></ThumbAction>
              </DrawingThumbOverlay>
            </DrawingThumb>
          ))}
        </DrawingGrid>
      )}

      {drawings.length < maxDrawings && (
        <AddDrawingBtn theme={theme} onClick={handleAdd}>
          + Add Drawing
        </AddDrawingBtn>
      )}

      {modalOpen && (
        <DrawingCanvas
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditIndex(null); }}
          existingBlobPath={editIndex !== null ? drawings[editIndex] : null}
          userId={userId}
        />
      )}

      {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}

      <ConfirmDialog
        isOpen={confirmDeleteIndex !== null}
        title="Remove Drawing"
        message="Are you sure you want to remove this drawing?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={() => {
          onChange(drawings.filter((_, idx) => idx !== confirmDeleteIndex));
          setConfirmDeleteIndex(null);
        }}
        onCancel={() => setConfirmDeleteIndex(null)}
      />
    </FieldContainer>
  );
};

// ==================== Geolocation sub-component ====================

/**
 * GeolocationFieldRenderer — manages state for a single geolocation field.
 * Must be a separate component so it can use useState.
 */
const GeolocationFieldRenderer = ({ field, value, onChange, errors }) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const { id, props } = field;
  const fieldError = errors?.[id];

  const locationData =
    value && typeof value === 'object' && value.latitude != null ? value : null;

  const handleSave = (data) => {
    onChange(data);
    setModalOpen(false);
  };

  const handleUseGps = (e) => {
    e.stopPropagation();
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          locationName: null,
        });
      },
      () => alert('Unable to get location. Please enable location services.')
    );
  };

  return (
    <FieldContainer theme={theme}>
      <LabelRow theme={theme}>
        {renderFieldTypeIcon(field.type, 13)}
        <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
      </LabelRow>

      {locationData ? (
        <div>
          <MiniMap
            latitude={locationData.latitude}
            longitude={locationData.longitude}
            accuracy={locationData.accuracy}
            height="200px"
            showButtons={false}
          />
          {locationData.locationName && (
            <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.xs }}>
              {locationData.locationName}
            </ThemedText>
          )}
          <div style={{ marginTop: theme.spacing.sm }}>
            <ChangeLocationBtn theme={theme} onClick={() => setModalOpen(true)}>
              <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Change Location
            </ChangeLocationBtn>
          </div>
        </div>
      ) : (
        <FileUploadContainer theme={theme} onClick={() => setModalOpen(true)}>
          <div style={{ marginBottom: theme.spacing.sm }}><MapPin size={32} /></div>
          <ThemedText>{props.placeholder || 'Pick Location on Map'}</ThemedText>
          <ThemedText type="small" style={{ color: theme.colors.muted }}>
            Click to open map, or{' '}
            <span
              onClick={handleUseGps}
              style={{ color: theme.colors.tint, cursor: 'pointer', textDecoration: 'underline' }}
            >
              use GPS
            </span>
          </ThemedText>
        </FileUploadContainer>
      )}

      {modalOpen && (
        <LocationPickerModal
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          initialValue={locationData}
        />
      )}

      {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
    </FieldContainer>
  );
};

/**
 * RepeatedFormFieldRenderer - Renders a repeated form field with multiple instances
 * Now using table display with separate page workflow
 */
const RepeatedFormFieldRenderer = ({ field, value = [], onChange, errors, formData, onBeforeNavigate }) => {
  const theme = useTheme();
  const [repeatedSchema, setRepeatedSchema] = useState(null);
  const [loading, setLoading] = useState(true);

  const { id, props } = field;
  const config = props.configuration || {};
  const { repeatedFormSchemaId, minInstances = 0, maxInstances = 10, repeatedFormDisplayName } = config;

  // Load repeated form schema
  useEffect(() => {
    const loadRepeatedFormSchema = async () => {
      if (!repeatedFormSchemaId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const formSchema = await formSchemaService.getFormSchemaById(repeatedFormSchemaId);

        // Convert schema definition to fields
        if (formSchema.schemaDefinition) {
          const fields = convertFormSchemaDefinitionToFields(formSchema.schemaDefinition);
          setRepeatedSchema({ ...formSchema, fields });
        }
      } catch (error) {
        console.error('Failed to load repeated form schema:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRepeatedFormSchema();
  }, [repeatedFormSchemaId]);

  // Ensure value is array
  const instances = Array.isArray(value) ? value : [];

  if (loading) {
    return (
      <FieldContainer theme={theme}>
        <LabelRow theme={theme}>
          {renderFieldTypeIcon(field.type, 13)}
          <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
        </LabelRow>
        <ThemedText style={{ color: theme.colors.muted }}>Loading repeated form...</ThemedText>
      </FieldContainer>
    );
  }

  if (!repeatedFormSchemaId || !repeatedSchema) {
    return (
      <FieldContainer theme={theme}>
        <LabelRow theme={theme}>
          {renderFieldTypeIcon(field.type, 13)}
          <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
        </LabelRow>
        <ErrorText theme={theme}>
          No repeated form template configured. Please configure this field in the form builder.
        </ErrorText>
      </FieldContainer>
    );
  }

  const displayName = repeatedFormDisplayName || repeatedSchema.name || 'Form Instance';

  // Use new table-based display with separate page workflow
  return (
    <RepeatedFormTableDisplay
      instances={instances}
      subformSchema={repeatedSchema.fields}
      fieldId={id}
      fieldLabel={displayName}
      minInstances={minInstances}
      maxInstances={maxInstances}
      onChange={onChange}
      errors={errors}
      formData={formData}
      onBeforeNavigate={onBeforeNavigate}
      repeatedFormSchemaId={repeatedFormSchemaId}
      allowLinkExisting={config.allowLinkExisting !== false}
      allowCreateNew={config.allowCreateNew !== false}
    />
  );
};

/**
 * DynamicFormRenderer - Renders editable form fields based on schema
 * @param {Object} props
 * @param {Array} props.schema - Form schema (array of field objects)
 * @param {Object} props.formData - Current form data values
 * @param {Function} props.onChange - Callback when form data changes
 * @param {Object} props.errors - Validation errors by field ID
 * @param {Function} props.onBeforeNavigate - Callback for pre-navigation draft save
 */
/**
 * Build the canonical `fields` array the shared rule evaluator expects:
 * `[{ key, options, rules }]` keyed on the internal field id.
 */
const toCanonicalFields = (schema) =>
  (Array.isArray(schema) ? schema : []).map(f => ({
    key: f.id,
    options: f.props?.options,
    rules: f.props?.rules,
  }));

export const DynamicFormRenderer = ({ schema, formData = {}, onChange, errors = {}, onBeforeNavigate, formSchemaId, attachments = [] }) => {
  const theme = useTheme();
  const { user } = useAuth();

  // ── Advanced Field Rules — evaluate visibility / cascade / format ─────────
  const canonicalFields = useMemo(() => toCanonicalFields(schema), [schema]);

  const ruleResult = useMemo(
    () => evaluateRules(canonicalFields, formData),
    [canonicalFields, formData]
  );
  const { hiddenFieldKeys = [], optionFilters = {}, fieldStyles = {} } = ruleResult;
  const hiddenKeySet = useMemo(() => new Set(hiddenFieldKeys), [hiddenFieldKeys]);

  /** Filter a field's options down to the cascade-allowed set, if any. */
  const filteredOptions = (fieldId, options) => {
    const allowed = optionFilters[fieldId];
    if (!Array.isArray(allowed) || !Array.isArray(options)) return options || [];
    const allowedSet = new Set(allowed);
    return options.filter(opt => allowedSet.has(opt.value));
  };

  // Auto-capture current date/time for fields with autoCapture enabled
  useEffect(() => {
    if (!schema || schema.length === 0) return;

    const autoCaptureFields = schema.filter(
      field => field.type === 'datetime' && field.props?.autoCapture
    );

    if (autoCaptureFields.length === 0) return;

    const now = new Date();
    const updates = {};
    let hasUpdates = false;
    const pad = n => String(n).padStart(2, '0');

    autoCaptureFields.forEach(field => {
      // Only auto-fill if field is empty
      if (!formData[field.id]) {
        const precision = field.props?.precision || 'datetime';
        if (precision === 'date') {
          // Format: YYYY-MM-DD
          updates[field.id] = now.toISOString().split('T')[0];
        } else if (precision === 'time') {
          // Format: HH:MM
          updates[field.id] = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
        } else {
          // Format: YYYY-MM-DDTHH:MM (datetime-local input)
          updates[field.id] = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
        }
        hasUpdates = true;
      }
    });

    if (hasUpdates) {
      onChange({ ...formData, ...updates });
    }
  }, [schema]); // Only run when schema changes, not on every formData change

  const handleFieldChange = (fieldId, value, fieldType = null) => {
    // Convert value based on field type
    let processedValue = value;

    // Handle numeric field types - convert strings to numbers
    if (fieldType === 'number' || fieldType === 'currency' || fieldType === 'percentage' || fieldType === 'slider' || fieldType === 'rating') {
      if (value === '' || value === null || value === undefined) {
        processedValue = null;
      } else {
        const numValue = Number(value);
        processedValue = isNaN(numValue) ? value : numValue;
      }
    }

    // Handle boolean field types - ensure proper boolean values
    if (fieldType === 'toggle') {
      processedValue = Boolean(value);
    }

    // Handle checkbox_group - ensure array value
    if (fieldType === 'checkbox_group') {
      processedValue = Array.isArray(value) ? value : [];
    }

    let newFormData = { ...formData, [fieldId]: processedValue };

    // Orphan clear: after the change, re-evaluate cascade filters and drop any
    // cascade-field value that is no longer within its allowed option set.
    // Iterate to a fixed point (bounded by field count) since clearing one
    // controller can re-open or close another field's filter.
    const fieldCount = canonicalFields.length;
    for (let pass = 0; pass <= fieldCount; pass++) {
      const { optionFilters: nextFilters, hiddenFieldKeys: nextHidden } = evaluateRules(canonicalFields, newFormData);
      let changed = false;
      for (const key of Object.keys(nextFilters)) {
        const allowed = nextFilters[key];
        const current = newFormData[key];
        if (!Array.isArray(allowed)) continue;
        const allowedSet = new Set(allowed);
        if (Array.isArray(current)) {
          const kept = current.filter(v => allowedSet.has(v));
          if (kept.length !== current.length) {
            newFormData = { ...newFormData, [key]: kept };
            changed = true;
          }
        } else if (current !== undefined && current !== null && current !== ''
          && !allowedSet.has(current)) {
          newFormData = { ...newFormData, [key]: '' };
          changed = true;
        }
      }
      // Clear values of fields hidden by a visibility rule so dependent (nested)
      // fields re-evaluate and collapse — a stale value on a now-hidden controller
      // would otherwise keep its children visible.
      for (const key of (nextHidden || [])) {
        const current = newFormData[key];
        const empty = current === undefined || current === null || current === ''
          || (Array.isArray(current) && current.length === 0);
        if (!empty) {
          newFormData = { ...newFormData, [key]: Array.isArray(current) ? [] : '' };
          changed = true;
        }
      }
      if (!changed) break;
    }

    onChange(newFormData);
  };

  const renderField = (field) => {
    const { id, type, props } = field;
    // Fields marked hidden (e.g., auto-populated lab results) are omitted from the fill form.
    // They remain in the schema for detail view, filters, analytics, and exports.
    if (props?.isHidden) return null;
    // A visibility rule may also hide this field based on other answers.
    if (hiddenKeySet.has(id)) return null;

    // Format rule — colour the input and surface a badge near the label.
    const fieldStyle = fieldStyles[id];
    const dynamicColor = fieldStyle && (fieldStyle.bg || fieldStyle.fg)
      ? { bg: fieldStyle.bg, fg: fieldStyle.fg }
      : undefined;
    const flagBadge = fieldStyle?.flag
      ? <FieldFlagBadge theme={theme} $bg={fieldStyle.bg} $fg={fieldStyle.fg}>{fieldStyle.flag}</FieldFlagBadge>
      : null;
    // Use appropriate default values based on field type
    let fieldValue = formData[id];
    if (fieldValue === undefined || fieldValue === null) {
      // Default to false for boolean fields, empty array for checkbox_group, empty string for others
      if (type === 'toggle') {
        fieldValue = false;
      } else if (type === 'checkbox_group' || type === 'tags') {
        fieldValue = [];
      } else {
        fieldValue = '';
      }
    }

    // Parse checkbox_group / tags JSON strings
    if ((type === 'checkbox_group' || type === 'tags') && typeof fieldValue === 'string') {
      try {
        const parsed = JSON.parse(fieldValue);
        fieldValue = Array.isArray(parsed) ? parsed : (fieldValue ? fieldValue.split(',').map(t => t.trim()).filter(Boolean) : []);
      } catch (e) {
        // Fallback: treat as comma-separated (legacy format)
        fieldValue = fieldValue ? fieldValue.split(',').map(t => t.trim()).filter(Boolean) : [];
      }
    }

    const fieldError = errors[id];

    switch (type) {
      case 'input':
        return (
          <FieldContainer key={id} theme={theme}>
            <FieldSuggestInput
              label={<>{renderFieldTypeIcon(type, 13, { style: { verticalAlign: 'middle', marginRight: 3 } })}{props.label + (props.required ? ' *' : '')}{flagBadge}</>}
              placeholder={props.placeholder}
              value={fieldValue}
              onChange={(val) => handleFieldChange(id, val)}
              required={props.required}
              formSchemaId={formSchemaId}
              fieldKey={id}
              dynamicColor={dynamicColor}
            />
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );

      case 'number':
        return (
          <FieldContainer key={id} theme={theme}>
            <Input
              label={<>{renderFieldTypeIcon(type, 13, { style: { verticalAlign: 'middle', marginRight: 3 } })}{props.label + (props.required ? ' *' : '')}{flagBadge}</>}
              type="number"
              step="any"
              placeholder={props.placeholder}
              value={fieldValue}
              onChange={(e) => handleFieldChange(id, e.target.value, type)}
              required={props.required}
              dynamicColor={dynamicColor}
            />
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );

      case 'date':
      case 'time':
      case 'datetime': {
        const precision = props?.precision || type;
        const htmlType = precision === 'date' ? 'date'
                       : precision === 'time' ? 'time'
                       : 'datetime-local';
        return (
          <FieldContainer key={id} theme={theme}>
            <Input
              label={<>{renderFieldTypeIcon(type, 13, { style: { verticalAlign: 'middle', marginRight: 3 } })}{props.label + (props.required ? ' *' : '')}{flagBadge}</>}
              type={htmlType}
              value={fieldValue}
              onChange={(e) => handleFieldChange(id, e.target.value)}
              required={props.required}
              dynamicColor={dynamicColor}
            />
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );
      }

      case 'select':
        return (
          <FieldContainer key={id} theme={theme}>
            <LabelRow theme={theme}>
              {renderFieldTypeIcon(type, 13)}
              <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
              {flagBadge}
            </LabelRow>
            <StyledSelect
              theme={theme}
              value={fieldValue}
              onChange={(e) => handleFieldChange(id, e.target.value)}
              required={props.required}
              $dynamicBg={dynamicColor?.bg}
              $dynamicFg={dynamicColor?.fg}
            >
              <option value="">Choose...</option>
              {filteredOptions(id, props.options).map((opt, idx) => (
                <option key={idx} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </StyledSelect>
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );

      case 'textarea':
        return (
          <FieldContainer key={id} theme={theme}>
            <Input
              label={<>{renderFieldTypeIcon(type, 13, { style: { verticalAlign: 'middle', marginRight: 3 } })}{props.label + (props.required ? ' *' : '')}{flagBadge}</>}
              type="textarea"
              placeholder={props.placeholder}
              value={fieldValue}
              onChange={(e) => handleFieldChange(id, e.target.value)}
              required={props.required}
              rows={props.rows || 3}
              dynamicColor={dynamicColor}
            />
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );

      case 'toggle':
        return (
          <FieldContainer key={id} theme={theme}>
            <LabelRow theme={theme}>
              {renderFieldTypeIcon(type, 13)}
              <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
            </LabelRow>
            <CheckboxContainer theme={theme}>
              <StyledCheckbox
                theme={theme}
                type="checkbox"
                checked={!!fieldValue}
                onChange={(e) => handleFieldChange(id, e.target.checked, type)}
              />
              <ThemedText>{fieldValue ? 'Yes' : 'No'}</ThemedText>
            </CheckboxContainer>
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );

      case 'checkbox_group': {
        const cgOptions = filteredOptions(id, props.options);
        return (
          <FieldContainer key={id} theme={theme}>
            <LabelRow theme={theme}>
              {renderFieldTypeIcon(type, 13)}
              <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
              {flagBadge}
            </LabelRow>
            {cgOptions && cgOptions.length > 0 ? (
              <OptionsGroupBox theme={theme}>
                {cgOptions.map((opt, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <StyledCheckbox
                      theme={theme}
                      type="checkbox"
                      value={opt.value}
                      checked={Array.isArray(fieldValue) && fieldValue.includes(opt.value)}
                      onChange={(e) => {
                        const currentValues = Array.isArray(fieldValue) ? [...fieldValue] : [];
                        if (e.target.checked) {
                          handleFieldChange(id, [...currentValues, opt.value], type);
                        } else {
                          handleFieldChange(id, currentValues.filter(v => v !== opt.value), type);
                        }
                      }}
                    />
                    <ThemedText>{opt.label}</ThemedText>
                  </div>
                ))}
              </OptionsGroupBox>
            ) : (
              <ThemedText style={{ color: theme.colors.muted, fontStyle: 'italic' }}>
                No options configured for this checkbox group
              </ThemedText>
            )}
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );
      }

      case 'radio':
        return (
          <FieldContainer key={id} theme={theme}>
            <LabelRow theme={theme}>
              {renderFieldTypeIcon(type, 13)}
              <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
              {flagBadge}
            </LabelRow>
            <OptionsGroupBox theme={theme}>
              {filteredOptions(id, props.options).map((opt, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                  <StyledRadio
                    theme={theme}
                    type="radio"
                    name={id}
                    value={opt.value}
                    checked={fieldValue === opt.value}
                    onChange={(e) => handleFieldChange(id, e.target.value)}
                  />
                  <ThemedText>{opt.label}</ThemedText>
                </div>
              ))}
            </OptionsGroupBox>
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );

      case 'tags': {
        const selectedTags = Array.isArray(fieldValue) ? fieldValue : [];
        const predefinedOptions = props.options || [];
        const allowCustom = props.allowCustom !== false;
        const maxTags = props.maxTags || null;
        const atMax = maxTags !== null && selectedTags.length >= maxTags;

        return (
          <FieldContainer key={id} theme={theme}>
            <LabelRow theme={theme}>
              {renderFieldTypeIcon(type, 13)}
              <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
            </LabelRow>

            {/* Bordered container: selected chips + text input */}
            <TagFieldContainer theme={theme}>
              {selectedTags.length > 0 && (
                <TagChipsRow theme={theme}>
                  {selectedTags.map(tag => {
                    const opt = predefinedOptions.find(o => o.value === tag);
                    const color = opt?.color || theme.colors.tint;
                    const label = opt?.label || tag;
                    return (
                      <TagChip key={tag} color={color} theme={theme}>
                        {label}
                        <TagChipRemove onClick={() => handleFieldChange(id, selectedTags.filter(t => t !== tag))}><XIcon size={12} /></TagChipRemove>
                      </TagChip>
                    );
                  })}
                </TagChipsRow>
              )}

              {allowCustom && (
                <TagTextInput
                  placeholder={atMax ? `Max ${maxTags} tags reached` : (props.placeholder || 'Type and press Enter...')}
                  disabled={atMax}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      e.preventDefault();
                      const v = e.target.value.trim();
                      if (!selectedTags.includes(v) && !atMax) {
                        handleFieldChange(id, [...selectedTags, v]);
                      }
                      e.target.value = '';
                    }
                  }}
                  theme={theme}
                />
              )}
            </TagFieldContainer>

            {/* Predefined options (below the container) */}
            {predefinedOptions.filter(opt => !selectedTags.includes(opt.value)).length > 0 && (
              <TagChipsRow theme={theme}>
                {predefinedOptions
                  .filter(opt => !selectedTags.includes(opt.value))
                  .map(opt => (
                    <TagChipOutline
                      key={opt.value}
                      color={opt.color || theme.colors.tint}
                      theme={theme}
                      onClick={() => !atMax && handleFieldChange(id, [...selectedTags, opt.value])}
                      $disabled={atMax}
                    >
                      + {opt.label}
                    </TagChipOutline>
                  ))}
              </TagChipsRow>
            )}

            {maxTags && (
              <ThemedText type="small" style={{ color: theme.colors.muted }}>
                {selectedTags.length}/{maxTags} tags selected
              </ThemedText>
            )}
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );
      }

      case 'image_camera':
        return (
          <FieldContainer key={id} theme={theme}>
            <LabelRow theme={theme}>
              {renderFieldTypeIcon(type, 13)}
              <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
            </LabelRow>
            <FileUpload
              value={Array.isArray(fieldValue) ? fieldValue : (fieldValue ? [fieldValue] : [])}
              onChange={(blobPaths) => handleFieldChange(id, blobPaths)}
              userId={user?.id || 0}
              maxFiles={5}
              accept="image/*"
            />
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );

      case 'geolocation':
        return (
          <GeolocationFieldRenderer
            key={id}
            field={field}
            value={fieldValue}
            onChange={(val) => handleFieldChange(id, val)}
            errors={errors}
          />
        );

      case 'drawing':
        return (
          <DrawingFieldRenderer
            key={id}
            field={field}
            value={fieldValue}
            onChange={(val) => handleFieldChange(id, val)}
            errors={errors}
            userId={user?.id || 0}
          />
        );

      case 'voice':
        return (
          <FieldContainer key={id} theme={theme}>
            <LabelRow theme={theme}>
              {renderFieldTypeIcon(type, 13)}
              <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
            </LabelRow>
            <VoiceFieldRenderer
              field={field}
              value={fieldValue}
              onChange={(val) => handleFieldChange(id, val)}
              attachment={attachments.find(a => a.fieldName === field.id)}
            />
            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );

      case 'qr_scanner':
      case 'barcode_scanner':
        const scannerType = type === 'qr_scanner' ? 'QR Code' : 'Barcode';
        const ScannerIcon = type === 'qr_scanner' ? QrCode : Tag;

        return (
          <FieldContainer key={id} theme={theme}>
            <LabelRow theme={theme}>
              {renderFieldTypeIcon(type, 13)}
              <Label theme={theme}>{props.label + (props.required ? ' *' : '')}</Label>
            </LabelRow>

            <Input
              type="text"
              placeholder={props.placeholder || `Enter ${scannerType}...`}
              value={fieldValue || ''}
              onChange={(e) => {
                handleFieldChange(id, e.target.value, type);
              }}
              required={props.required}
            />

            <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.xs }}>
              <ScannerIcon size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{scannerType} scanner (Manual entry for web version - scanning available in mobile app)
            </ThemedText>

            {props.validationRules?.rules?.length > 0 && (
              <ThemedText type="small" style={{ color: theme.colors.info, marginTop: theme.spacing.xs }}>
                <Info size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{props.validationRules.rules.length} validation rule(s) will be applied when scanned
              </ThemedText>
            )}

            {fieldError && <ErrorText theme={theme}>{fieldError}</ErrorText>}
          </FieldContainer>
        );

      case 'repeated_form':
        return (
          <RepeatedFormFieldRenderer
            key={id}
            field={field}
            value={fieldValue}
            onChange={(value) => handleFieldChange(id, value, type)}
            errors={fieldError}
            formData={formData}
            onBeforeNavigate={onBeforeNavigate}
          />
        );

      case 'divider':
        return (
          <hr
            key={id}
            style={{
              border: 'none',
              height: '1px',
              backgroundColor: theme.colors.border,
              margin: `${theme.spacing.lg} 0`
            }}
          />
        );

      case 'header':
        const HeaderTag = props.level || 'h3';
        return (
          <ThemedText
            key={id}
            as={HeaderTag}
            type="subtitle"
            style={{
              textAlign: props.align || 'left',
              marginTop: theme.spacing.md,
              marginBottom: theme.spacing.md
            }}
          >
            {props.text}
          </ThemedText>
        );

      default:
        return (
          <FieldContainer key={id} theme={theme}>
            <ThemedText style={{ color: theme.colors.warning }}>
              Unknown field type: {type}
            </ThemedText>
          </FieldContainer>
        );
    }
  };

  return (
    <FormContainer theme={theme}>
      {schema && schema.length > 0 ? (
        schema.map(field => renderField(field))
      ) : (
        <ThemedText style={{ color: theme.colors.muted }}>
          No form fields available
        </ThemedText>
      )}
    </FormContainer>
  );
};

const TagFieldContainer = styled.div`
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  padding: ${p => p.theme.spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.xs};
  min-height: 44px;
  &:focus-within { border-color: ${p => p.theme.colors.tint}; }
`;

const TagChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
`;

const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.color || props.theme.colors.tint};
  color: #fff;
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 3px 10px;
  font-size: ${props => props.theme.typography.sizes.small};
`;

const TagChipRemove = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  line-height: 1;
  opacity: 0.8;
  &:hover { opacity: 1; }
`;

const TagChipOutline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  color: ${props => props.color || props.theme.colors.tint};
  border: 1.5px solid ${props => props.color || props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 3px 10px;
  font-size: ${props => props.theme.typography.sizes.small};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: background 0.15s;
  &:hover {
    background: ${props => props.$disabled ? 'transparent' : (props.color || props.theme.colors.tint) + '18'};
  }
`;

const TagTextInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  padding: ${p => p.theme.spacing.xs};
  font-size: ${p => p.theme.typography.sizes.default};
  background: transparent;
  color: ${p => p.theme.colors.text};
  font-family: ${p => p.theme.typography.fontFamily};
  &::placeholder { color: ${p => p.theme.colors.muted}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export default DynamicFormRenderer;
